/**
 * Service pour gérer les fermetures adaptatives
 * Calcule automatiquement la longueur de fermeture selon la forme
 * et trouve la fermeture la plus appropriée dans la liste
 */

import { parametreService, Fourniture } from './ParametreService';

export interface FermetureAdaptative {
  id: string;
  type: 'glissiere' | 'velcro';
  longueurCalculee: number; // Longueur calculée selon la forme
  longueurFourniture: number; // Longueur de la fourniture sélectionnée
  prixUnitaire: number;
  tempsMontageMajore: number; // Temps de montage avec majoration selon la longueur
  fournitureSelectionnee: Fourniture;
  ecartLongueur: number; // Écart entre longueur calculée et fourniture
}

export interface CalculFermetureParams {
  forme: string;
  dimensions: {
    longueur?: number;
    largeur?: number;
    diametre?: number;
    hauteur?: number;
  };
  typeFermeture: 'glissiere' | 'velcro';
}

class FermetureService {
  /**
   * Calcule la longueur de fermeture selon la forme de l'article
   */
  calculerLongueurFermeture(params: CalculFermetureParams): number {
    const { forme, dimensions } = params;


    switch (forme) {
      case 'rectangulaire_bandeau':
      case 'rectangulaire_joues':
        // Pour les rectangles : prendre la plus grande dimension (longueur ou largeur)
        return Math.max(dimensions.longueur || 0, dimensions.largeur || 0);
      
      case 'rond_bandeau':
      case 'rond_avec_bandeau':
        // Pour les ronds : demi-circonférence pour faciliter l'ouverture
        const longueurCalculee = Math.round((dimensions.diametre || 0) * Math.PI / 2);

        return longueurCalculee;
      
      case 'cylindre_joues':
        // Pour les cylindres : circonférence complète ou longueur selon l'usage
        // On prend la longueur pour une ouverture latérale
        return dimensions.longueur || 0;
      
      case 'rectangle_simple':
        // Pour les bâches : généralement sur la largeur pour faciliter le pliage
        return dimensions.largeur || 0;
      
      default:
        // Par défaut, prendre la longueur
        return dimensions.longueur || 0;
    }
  }

  /**
   * Trouve la fermeture la plus appropriée dans la liste des fournitures
   */
  async trouverFermetureOptimale(
    longueurCalculee: number, 
    typeFermeture: 'glissiere' | 'velcro'
  ): Promise<Fourniture | null> {
    try {
      // Utiliser le ParametreService qui fonctionne déjà
      const fournitures = await parametreService.getFournitures();


      // Filtrer les fermetures du type demandé
      const fermetures = fournitures.filter(f => 
        f.type === typeFermeture && 
        typeof (f as any).taille === 'number'
      );

      if (fermetures.length === 0) {
        console.warn(`❌ Aucune fermeture ${typeFermeture} trouvée dans les ${fournitures.length} fournitures`);
        console.log('Types disponibles:', Array.from(new Set(fournitures.map(f => f.type))));
        return null;
      }
      
      // Trier par taille croissante
      fermetures.sort((a, b) => ((a as any).taille || 0) - ((b as any).taille || 0));
      
      // Stratégie de sélection :
      // 1. Chercher une fermeture de taille exacte
      let fermetureOptimale = fermetures.find((f: any) => f.taille === longueurCalculee);
      
      if (fermetureOptimale) {
        return fermetureOptimale;
      }
      
      // 2. Chercher la plus petite fermeture supérieure à la longueur calculée
      fermetureOptimale = fermetures.find((f: any) => f.taille >= longueurCalculee);
      
      if (fermetureOptimale) {
        return fermetureOptimale;
      }
      
      // 3. Si aucune fermeture n'est assez grande, prendre la plus grande disponible
      return fermetures[fermetures.length - 1];
      
    } catch (error) {
      console.error('Erreur détaillée lors de la recherche de fermeture optimale:');
      console.error('- Message:', (error as Error).message);
      console.error('- Type:', (error as Error).constructor.name);
      console.error('- Stack:', (error as Error).stack);
      
      // Différencier les types d'erreurs
      if ((error as Error).message.includes('fetch')) {
        console.error('🌐 Erreur réseau - vérifiez la connexion au backend');
      } else if ((error as Error).message.includes('JSON')) {
        console.error('📄 Erreur de parsing - format de données invalide');
      } else if ((error as Error).message.includes('données')) {
        console.error('🔍 Erreur de validation - structure de données incorrecte');
      }
      
      return null;
    }
  }

  /**
   * Calcule le temps de montage majoré selon la longueur de la fermeture
   */
  calculerTempsMontage(longueurFermeture: number, tempsBase: number): number {
    // Majoration progressive selon la longueur :
    // - Jusqu'à 30 cm : temps de base
    // - 30-60 cm : +20%
    // - 60-100 cm : +40%
    // - Plus de 100 cm : +60%
    
    let coefficient = 1.0;
    
    if (longueurFermeture > 100) {
      coefficient = 1.6;
    } else if (longueurFermeture > 60) {
      coefficient = 1.4;
    } else if (longueurFermeture > 30) {
      coefficient = 1.2;
    }
    
    return Math.round(tempsBase * coefficient);
  }

  /**
   * Calcule une fermeture adaptative complète
   */
  async calculerFermetureAdaptative(params: CalculFermetureParams): Promise<FermetureAdaptative | null> {
    try {
      // 1. Calculer la longueur nécessaire
      const longueurCalculee = this.calculerLongueurFermeture(params);
      
      if (longueurCalculee <= 0) {
        return null;
      }
      
      // 2. Trouver la fermeture optimale
      const fournitureSelectionnee = await this.trouverFermetureOptimale(
        longueurCalculee, 
        params.typeFermeture
      );
      
      if (!fournitureSelectionnee) {
        return null;
      }
      
      // 3. Récupérer les tarifs pour le temps de base
      const tarifsCoefficients = await parametreService.getTarifsCoefficients();
      const tempsCoutureGlissiere = tarifsCoefficients.find(
        t => t.nom === 'Temps couture glissière'
      )?.valeur || 15;
      
      // 4. Calculer le temps de montage majoré
      const longueurFourniture = (fournitureSelectionnee as any).taille || longueurCalculee;
      const tempsMontageMajore = this.calculerTempsMontage(longueurFourniture, tempsCoutureGlissiere);
      
      // 5. Calculer l'écart
      const ecartLongueur = Math.abs(longueurFourniture - longueurCalculee);
      
      return {
        id: `fermeture-${Date.now()}`,
        type: params.typeFermeture,
        longueurCalculee,
        longueurFourniture,
        prixUnitaire: fournitureSelectionnee.prix,
        tempsMontageMajore,
        fournitureSelectionnee,
        ecartLongueur
      };
      
    } catch (error) {
      console.error('Erreur lors du calcul de fermeture adaptative:', error);
      return null;
    }
  }

  /**
   * Obtient la liste des fermetures disponibles par type
   */
  async getFermeturesDisponibles(type?: 'glissiere' | 'velcro'): Promise<Fourniture[]> {
    try {
      const fournitures = await parametreService.getFournitures();
      
      let fermetures = fournitures.filter(f => 
        (f.type === 'glissiere' || f.type === 'velcro') &&
        typeof (f as any).taille === 'number'
      );
      
      if (type) {
        fermetures = fermetures.filter(f => f.type === type);
      }
      
      // Trier par taille croissante
      fermetures.sort((a, b) => ((a as any).taille || 0) - ((b as any).taille || 0));
      
      return fermetures;
      
    } catch (error) {
      console.error('Erreur lors de la récupération des fermetures:', error);
      return [];
    }
  }

  /**
   * Formate l'affichage d'une fermeture pour l'interface utilisateur
   */
  formaterAffichageFermeture(fermeture: FermetureAdaptative): string {
    const typeLabel = fermeture.type === 'glissiere' ? 'Glissière' : 'Velcro';
    const ecartInfo = fermeture.ecartLongueur > 0 
      ? ` (${fermeture.ecartLongueur} cm de plus)` 
      : '';
    
    return `${typeLabel} ${fermeture.longueurFourniture} cm - ${fermeture.prixUnitaire.toFixed(2)}€${ecartInfo}`;
  }

  /**
   * Vérifie si une fermeture nécessite une attention particulière
   */
  verifierAlerteFermeture(fermeture: FermetureAdaptative): {
    hasAlert: boolean;
    message?: string;
    type?: 'warning' | 'info';
  } {
    // Alerte si l'écart est important (plus de 20 cm)
    if (fermeture.ecartLongueur > 20) {
      return {
        hasAlert: true,
        message: `La fermeture sélectionnée (${fermeture.longueurFourniture} cm) est ${fermeture.ecartLongueur} cm plus grande que nécessaire (${fermeture.longueurCalculee} cm)`,
        type: 'warning'
      };
    }
    
    // Info si la fermeture est parfaitement adaptée
    if (fermeture.ecartLongueur === 0) {
      return {
        hasAlert: true,
        message: `Fermeture parfaitement adaptée à la taille calculée`,
        type: 'info'
      };
    }
    
    return { hasAlert: false };
  }
}

// Exporter une instance unique du service
export const fermetureService = new FermetureService();
